import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { projects } from '../constants/data.js';
import { Container } from '../styles/components.js';
import { FaApple, FaGooglePlay, FaArrowLeft } from 'react-icons/fa';

const PageWrapper = styled.div`
  padding-top: 0;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

const HeaderSection = styled.div`
  background: ${({ color, theme }) =>
    `linear-gradient(135deg, ${color || theme.colors.primary}15 0%, ${theme.colors.background} 100%)`};
  padding: 120px 0 ${({ theme }) => theme.spacing.xxl};
  position: relative;
  overflow: hidden;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.textGray};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-weight: 500;
  transition: color 0.3s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ImageContainer = styled(motion.div)`
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.large};
  background-color: ${({ theme }) => theme.colors.surface};
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.5s ease;
  }

  &:hover img {
    transform: scale(1.02);
  }
`;

const ProjectInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  margin: 0 auto;
`;

const ProjectIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ color, theme }) => (color || theme.colors.primary) + '20'};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ color, theme }) => color || theme.colors.primary};
`;

const ProjectTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.heading.h2};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textDark};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.heading.h4};
  margin: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.textDark};
  font-weight: 700;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.textGray};
  font-size: ${({ theme }) => theme.typography.body.large};
  line-height: 1.8;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  white-space: pre-line;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Tag = styled.span`
  background-color: ${({ color, theme }) => (color || theme.colors.primary) + '15'};
  color: ${({ color, theme }) => color || theme.colors.primary};
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.body.small};
  font-weight: 600;
  border: 1px solid ${({ color, theme }) => (color || theme.colors.primary) + '30'};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const StoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 24px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textLight};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: ${({ theme }) => theme.shadows.medium};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.large};
    background-color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    font-size: 1.2rem;
  }
`;

const ProjectDetail = () => {
  const { id } = useParams();
  const project = projects.find(p => p.id === parseInt(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!project) return (
    <Container>
      <div style={{ padding: '100px 0', textAlign: 'center' }}>
        <h2>Project not found</h2>
        <BackButton to="/">Go Home</BackButton>
      </div>
    </Container>
  );

  return (
    <PageWrapper>
      <HeaderSection color={project.color}>
        <Container>
          <BackButton to="/#projects">
            <FaArrowLeft /> Back to Projects
          </BackButton>

          <ImageContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {project.coverImage && (
              <img src={project.coverImage} alt={project.title} />
            )}
          </ImageContainer>

          <ProjectInfo
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
              <ProjectIcon color={project.color}>
                {project.logo ? (
                  <img
                    src={project.logo}
                    alt="logo"
                    style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }}
                  />
                ) : (
                  project.icon
                )}
              </ProjectIcon>
              <ProjectTitle style={{ marginBottom: 0 }}>{project.title}</ProjectTitle>
            </div>

            <TagsContainer>
              {project.tags?.map((tag, index) => (
                <Tag key={index} color={project.color}>{tag}</Tag>
              ))}
            </TagsContainer>

            <Description>{project.description}</Description>

            {project.subscriptionContent && (
              <>
                <SectionTitle>Premium Features</SectionTitle>
                <Description>{project.subscriptionContent}</Description>
              </>
            )}

            <ButtonGroup>
              {project.link && (
                <StoreButton href={project.link} target="_blank" rel="noopener noreferrer">
                  <FaApple /> App Store
                </StoreButton>
              )}

              {project.androidLink && (
                <StoreButton href={project.androidLink} target="_blank" rel="noopener noreferrer">
                  <FaGooglePlay /> Google Play
                </StoreButton>
              )}
            </ButtonGroup>
          </ProjectInfo>
        </Container>
      </HeaderSection>
    </PageWrapper>
  );
};

export default ProjectDetail;
